import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon, TextInput, type TextInputProps } from '~/molecules';
import { FieldActionButton } from '../FieldActionButton';
import { ReadonlyField } from '../ReadonlyField';
import { FormControl } from '~/molecules/components/FormControl';
import { useTranslator } from '~/translations/TranslatorContext';
import { StyleSheet } from 'react-native-unistyles';
import { type StyleProp, type ViewStyle, type TextStyle } from 'react-native';
import { View, type PointerEvent } from 'react-native';

import { VariableSuggestions } from '../../VariableSuggestions/VariableSuggestionsMenu';
import { textInputEditor } from '../../VariableSuggestions/VariableEditor';
import { variableIconSvg } from '../../VariableSuggestions/variableIconSvg';
import type { FetchVariableSuggestions } from '../../VariableSuggestions/variableSuggestions';

const MIN_HEIGHT = 40;

export interface TextFieldProps {
    fetchSuggestions?: FetchVariableSuggestions;
    suggestionContextKey?: string;
    autoFocus?: boolean;
    autoComplete?: TextInputProps['autoComplete'];
    disableInput?: boolean;
    editable?: boolean;
    fieldType?: string;
    hideLabel?: boolean;
    icon?: ReactNode;
    iconLeft?: ReactNode;
    iconRight?: ReactNode;
    iconSize?: number;
    inputStyle?: StyleProp<TextStyle>;
    inputWrapperStyle?: StyleProp<ViewStyle>;
    inputContainerStyle?: StyleProp<ViewStyle>;
    isinvalid?: boolean;
    isReadonly?: boolean;
    multiline?: boolean;
    name?: string;
    onChange: (value: string) => void;
    onClick?: () => void;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    onKeyPress?: () => void;
    onSubmitEditing?: () => void;
    onBlur?: () => void;
    placeholder?: string;
    required?: boolean;
    title?: string;
    tooltip?: string;
    type?: string;
    validationMessage?: ReactNode;
    value?: string;
}

export const TextField = (props: TextFieldProps) => {
    const {
        autoComplete,
        autoFocus,
        disableInput,
        hideLabel,
        icon,
        iconLeft,
        iconRight,
        isinvalid,
        isReadonly,
        inputStyle,
        inputWrapperStyle,
        multiline,
        name,
        onChange,
        onKeyPress,
        onSubmitEditing,
        onBlur,
        placeholder,
        required,
        title,
        tooltip,
        type,
        validationMessage,
        value = '',
        inputContainerStyle,
    } = props;

    const trans = useTranslator();
    const [focused, setFocused] = useState(false);
    const [input, setInput] = useState<HTMLInputElement | HTMLTextAreaElement | null>(null);
    const change = useRef(onChange);
    change.current = onChange;
    const editor = useMemo(() => (input ? textInputEditor(input, (value) => change.current(value)) : null), [input]);
    const suggestionsEnabled =
        !!props.fetchSuggestions && !isReadonly && !disableInput && props.editable !== false && type !== 'password';

    styles.useVariants({ multiline: !!multiline });

    const valueToDisplay = useMemo(() => {
        if (isReadonly && type === 'password') {
            return { value: trans('passwordPlaceholder') };
        }
        if (type === 'password') {
            return { value, secureTextEntry: true };
        }
        return { value };
    }, [isReadonly, type, value, trans]);

    const handleChange = (nextValue: string) => {
        onChange(nextValue);
    };

    const showMultilineIcon = Boolean(multiline && !iconRight && !icon);

    const [multilineHeight, setMultilineHeight] = useState<number>(40);

    const maxHeightRef = useRef(150);
    const resizeAbortRef = useRef<AbortController | null>(null);

    const pointerStartYRef = useRef(0);
    const inputStartHeightRef = useRef(0);

    const stopResize = useCallback(() => {
        resizeAbortRef.current?.abort();
        resizeAbortRef.current = null;
    }, []);

    const handlePointerMove = useCallback((e: MouseEvent) => {
        const delta = e.pageY - pointerStartYRef.current;
        const next = Math.max(MIN_HEIGHT, Math.min(inputStartHeightRef.current + delta, maxHeightRef.current));

        setMultilineHeight(next);
    }, []);

    const handleResize = useCallback(
        (e: PointerEvent) => {
            if (!multiline || typeof window === 'undefined') return;
            stopResize();

            maxHeightRef.current = Math.floor(window.innerHeight * 0.5);
            inputStartHeightRef.current = multilineHeight;
            pointerStartYRef.current = e.nativeEvent.pageY;

            const controller = new AbortController();
            resizeAbortRef.current = controller;

            window.addEventListener('pointermove', handlePointerMove, { signal: controller.signal });
            window.addEventListener('pointerup', stopResize, { signal: controller.signal });
            window.addEventListener('pointercancel', stopResize, { signal: controller.signal });
        },
        [multiline, multilineHeight, handlePointerMove, stopResize]
    );

    useEffect(() => () => stopResize(), [stopResize]);

    const setInputRef = useCallback((node: unknown) => {
        setInput(node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement ? node : null);
    }, []);

    if (isReadonly) {
        return <ReadonlyField name={name} label={title} tooltip={tooltip} {...valueToDisplay} />;
    }

    const rightContent = showMultilineIcon ? (
        <Icon name="resize-bottom-right" type="material-community" size={20} style={styles.multilineIcon} />
    ) : (
        (icon ?? iconRight)
    );

    return (
        <FormControl disabled={disableInput || props.editable === false} style={[styles.wrapper, inputWrapperStyle]}>
            {!hideLabel && (
                <FormControl.Label error={validationMessage} label={title} required={required} tooltip={tooltip} />
            )}
            <TextInput
                ref={setInputRef}
                accessibilityLabel={name ?? title}
                autoComplete={autoComplete}
                autoFocus={autoFocus}
                disabled={disableInput || props.editable === false}
                error={!!isinvalid}
                inputStyle={[styles.inputStyles, multiline ? { minHeight: multilineHeight } : null, inputStyle]}
                multiline={multiline}
                onFocus={() => setFocused(true)}
                onBlur={() => {
                    setFocused(false);
                    onBlur?.();
                }}
                onChangeText={handleChange}
                onKeyPress={onKeyPress}
                onSubmitEditing={onSubmitEditing}
                placeholder={placeholder}
                placeholderTextColor={
                    disableInput || props.editable === false
                        ? styles.disabledText.color
                        : styles.placeholderTextColor.color
                }
                required={required}
                style={[styles.inputContainer, inputContainerStyle]}
                {...valueToDisplay}
            >
                {iconLeft && <TextInput.Left>{iconLeft}</TextInput.Left>}
                {(rightContent || suggestionsEnabled) && (
                    <TextInput.Right
                        style={suggestionsEnabled ? undefined : styles.rightIcon}
                        onPointerDown={suggestionsEnabled ? undefined : handleResize}
                    >
                        {suggestionsEnabled && editor && (
                            <VariableSuggestions
                                editor={editor}
                                fetchSuggestions={props.fetchSuggestions!}
                                contextKey={props.suggestionContextKey}
                                renderTrigger={({ open, expanded, ref }) => (
                                    <FieldActionButton
                                        focused={focused}
                                        size={18}
                                        name={variableIconSvg}
                                        type="svg"
                                        ref={(node: unknown) => {
                                            ref.current = node as HTMLElement;
                                        }}
                                        accessibilityLabel={trans('wysiwyg.insertVariable')}
                                        aria-haspopup="listbox"
                                        aria-expanded={expanded}
                                        onPointerDown={(event) => event.preventDefault()}
                                        onPress={open}
                                    />
                                )}
                            />
                        )}
                        {rightContent &&
                            (suggestionsEnabled ? (
                                <View onPointerDown={handleResize}>{rightContent}</View>
                            ) : (
                                rightContent
                            ))}
                    </TextInput.Right>
                )}
            </TextInput>
        </FormControl>
    );
};

const styles = StyleSheet.create((theme) => ({
    disabledText: { color: theme.colors.onSurfaceDisabled },
    wrapper: {
        width: '100%',
    },
    inputContainer: {
        width: '100%',
    },
    inputStyles: {
        variants: {
            multiline: {
                true: {
                    paddingVertical: theme.spacings['2_5'],
                    _web: {
                        'field-sizing': 'content',
                    },
                },
            },
        },
    },
    multilineIcon: {
        color: theme.colors.onSurfaceMuted,
        height: 20,
        width: 20,
    },
    rightIcon: {
        variants: {
            multiline: {
                true: {
                    alignSelf: 'flex-end',
                    paddingBottom: theme.spacings['1'],
                    _web: {
                        bottom: -2,
                        cursor: 'row-resize',
                        position: 'absolute',
                        right: theme.spacings['1_5'],
                    },
                },
            },
        },
    },
    placeholderTextColor: {
        color: theme.colors.onSurfaceVariant,
    },
}));
